import { useState, useEffect, useMemo } from "react";
import { X, Plus, Trash2, Zap } from "lucide-react";
import "./SplitPaymentOverlay.css";

function formatNaira(value) {
  const n = Number(value) || 0;
  return `₦${n.toLocaleString("en-NG", { maximumFractionDigits: 2 })}`;
}

let splitIdCounter = 0;
function nextSplitId() {
  splitIdCounter += 1;
  return `split-${Date.now()}-${splitIdCounter}`;
}

/**
 * SplitPaymentOverlay
 *
 * Full-screen overlay for dividing a sale's total across two or more
 * payment accounts (e.g. part cash, part POS). Paid / remaining / change
 * are calculated live as amounts are entered.
 *
 * Props:
 * - totalDue: number
 * - paymentAccounts: array — receivingAccount documents ({ _id, name, paymentType, ... })
 * - initialSplits: array — [{ id, accountId, amount, reference }]
 * - onClose: () => void
 * - onConfirm: (result) => void
 *     result = { splits:[{id, accountId, accountName, accountColor, provider, isPartner, paymentType, amount, reference}], paid, remaining, change }
 */
// True when this account is wired to a partner integration — selecting it
// triggers a real API call to the partner rather than just recording a
// manual payment.
function isPartnerAccount(account) {
  return Boolean(account?.isPartner?.hasIntgration);
}

function accountSubtitle(account) {
  if (!account) return null;
  switch (account.paymentType) {
    case "bankTransfer":
      return [account.bankName, account.accountNumber].filter(Boolean).join(" · ");
    case "POS":
    case "storelensePOS":
      return [account.provider, account.terminalId || account.storeLenseTerminalID].filter(Boolean).join(" · ");
    case "card":
      return [account.provider, account.terminalId].filter(Boolean).join(" · ");
    case "mobilePay":
      return [account.provider, account.walletNumber].filter(Boolean).join(" · ");
    default:
      return null;
  }
}

export default function SplitPaymentOverlay({ totalDue, paymentAccounts, initialSplits, onClose, onConfirm }) {
  console.log(paymentAccounts, initialSplits);

  const [splits, setSplits] = useState(() =>
    initialSplits && initialSplits.length
      ? initialSplits.map((s) => ({
          id: s.id || nextSplitId(),
          accountId: s.accountId,
          amount: s.amount,
          reference: s.reference || "",
          serialNumber: s.serialNumber || s.terminalId,
        }))
      : [
          {
            id: nextSplitId(),
            accountId: paymentAccounts[0]?._id,
            amount: totalDue,
            reference: "",
            serialNumber: paymentAccounts[0]?.serialNumber || paymentAccounts[0]?.terminalId,
          },
        ],
  );

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose]);

  const accountsById = useMemo(() => Object.fromEntries(paymentAccounts.map((a) => [a._id, a])), [paymentAccounts]);

  const paid = splits.reduce((sum, s) => sum + (Number(s.amount) || 0), 0);
  const remaining = Math.max(0, totalDue - paid);
  const change = Math.max(0, paid - totalDue);

  // The max a given row is allowed to hold: total due minus whatever every
  // other row currently claims. Keeps the sum of all rows from ever
  // exceeding the transaction total.
  const maxForRow = (id) => {
    const others = splits.reduce((sum, s) => (s.id === id ? sum : sum + (Number(s.amount) || 0)), 0);
    return Math.max(0, totalDue - others);
  };

  const usedAccountIds = new Set(splits.map((s) => s.accountId).filter(Boolean));
  const hasUnusedAccount = paymentAccounts.some((a) => !usedAccountIds.has(a._id));

  const updateSplit = (id, patch) => {
    setSplits((prev) =>
      prev.map((s) => {
        if (s.id !== id) return s;
        if (patch.amount !== undefined && patch.amount !== "") {
          const cap = maxForRow(id);
          const requested = Number(patch.amount);
          if (requested > cap) return { ...s, ...patch, amount: cap };
        }
        return { ...s, ...patch };
      }),
    );
  };

  const addSplit = () => {
    if (remaining <= 0 || !hasUnusedAccount) return;
    const nextAccount = paymentAccounts.find((a) => !usedAccountIds.has(a._id));

    setSplits((prev) => [
      ...prev,
      { id: nextSplitId(), accountId: nextAccount?._id, amount: remaining, reference: "", serialNumber: nextAccount.serialNumber || nextAccount.terminalId },
    ]);
  };

  const removeSplit = (id) => {
    setSplits((prev) => (prev.length > 1 ? prev.filter((s) => s.id !== id) : prev));
  };

  // Split payments must land on the total exactly, with no account used
  // twice — no under- or overpay, and no duplicate accounts.
  const noDuplicateAccounts = usedAccountIds.size === splits.length;
  const canConfirm = splits.every((s) => s.accountId && Number(s.amount) > 0) && remaining === 0 && paid === totalDue && noDuplicateAccounts;

  const handleConfirm = () => {
    if (!canConfirm) return;
    onConfirm({
      splits: splits.map((s) => ({
        ...s,
        accountName: accountsById[s.accountId]?.name,
        accountColor: accountsById[s.accountId]?.color,
        provider: accountsById[s.accountId]?.provider,
        isPartner: accountsById[s.accountId]?.isPartner || null,
        paymentType: accountsById[s.accountId]?.paymentType,
        amount: Number(s.amount) || 0,
        serialNumber: s.serialNumber || s.terminalId,
      })),
      paid,
      remaining,
      change,
    });
  };

  return (
    <div
      className='spo-backdrop'
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className='spo-panel' role='dialog' aria-modal='true' aria-label='Split payment'>
        <div className='spo-header'>
          <h2>Split payment</h2>
          <button type='button' className='spo-close' onClick={onClose} aria-label='Close'>
            <X size={18} strokeWidth={2} />
          </button>
        </div>

        <div className='spo-due'>
          <span>Amount due</span>
          <span>{formatNaira(totalDue)}</span>
        </div>

        <div className='spo-list'>
          {splits.map((split, idx) => {
            const account = accountsById[split.accountId];
            const needsReference = account && account.paymentType !== "cash";
            return (
              <div className='spo-row' key={split.id}>
                <div className='spo-row__header'>
                  <span>Payment {idx + 1}</span>
                  {splits.length > 1 && (
                    <button type='button' className='spo-row__remove' onClick={() => removeSplit(split.id)} aria-label='Remove payment'>
                      <Trash2 size={14} strokeWidth={2} />
                    </button>
                  )}
                </div>

                <div className='spo-row__fields'>
                  <div className='spo-field'>
                    <label htmlFor={`spo-account-${split.id}`}>Account</label>
                    <select id={`spo-account-${split.id}`} value={split.accountId} onChange={(e) => updateSplit(split.id, { accountId: e.target.value })}>
                      {paymentAccounts
                        .filter((a) => a._id === split.accountId || !usedAccountIds.has(a._id))
                        .map((a) => {
                          const sub = accountSubtitle(a);
                          return (
                            <option key={a._id} value={a._id}>
                              {a.name}
                              {sub ? ` — ${sub}` : ""}
                              {isPartnerAccount(a) ? " • Partner" : ""}
                            </option>
                          );
                        })}
                    </select>
                  </div>
                  <div className='spo-field'>
                    <label htmlFor={`spo-amount-${split.id}`}>Amount</label>
                    <input
                      id={`spo-amount-${split.id}`}
                      type='number'
                      value={split.amount}
                      min={0}
                      max={maxForRow(split.id)}
                      onChange={(e) => updateSplit(split.id, { amount: e.target.value })}
                      placeholder='0'
                    />
                  </div>
                </div>

                {needsReference && (
                  <div className='spo-field spo-field--reference'>
                    <label htmlFor={`spo-reference-${split.id}`}>Reference</label>
                    <input
                      id={`spo-reference-${split.id}`}
                      type='text'
                      value={split.reference}
                      onChange={(e) => updateSplit(split.id, { reference: e.target.value })}
                      placeholder='Transaction reference'
                    />
                  </div>
                )}

                {isPartnerAccount(account) && (
                  <div className='spo-partner-note'>
                    <Zap size={13} strokeWidth={2.5} />
                    Partner integration — this payment is confirmed automatically via {account.provider || "the partner API"}.10 NGN is charged
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <button type='button' className='spo-add' onClick={addSplit} disabled={remaining <= 0 || !hasUnusedAccount}>
          <Plus size={15} strokeWidth={2} />
          Add another payment
        </button>

        <div className='spo-summary'>
          <div className='spo-summary__row'>
            <span>Paid</span>
            <span>{formatNaira(paid)}</span>
          </div>
          <div className='spo-summary__row'>
            <span>Remaining</span>
            <span className={remaining > 0 ? "spo-summary__remaining--due" : ""}>{formatNaira(remaining)}</span>
          </div>
          <div className='spo-summary__row'>
            <span>Change</span>
            <span>{formatNaira(change)}</span>
          </div>
        </div>

        <div className='spo-actions'>
          <button type='button' className='spo-btn spo-btn--ghost' onClick={onClose}>
            Cancel
          </button>
          <button type='button' className='spo-btn spo-btn--primary' disabled={!canConfirm} onClick={handleConfirm}>
            Confirm split payment
          </button>
        </div>
      </div>
    </div>
  );
}
